import { ChangeDetectionStrategy, Component, inject, Input, OnChanges, SimpleChanges } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { ActionSheetController, ModalController } from '@ionic/angular';
import { select, Store } from '@ngrx/store';

import { SeriesOption } from 'echarts';
import { EChartsOption, TitleOption } from 'echarts/types/dist/shared';
import { cloneDeep, filter, first, floor, has, isEmpty, isNull, last, map, round, snakeCase } from 'lodash-es';
import { DateTime } from 'luxon';
import { combineLatest, Observable } from 'rxjs';

import { selectNonDigitCharacters } from '../../../../../../constants/regular-expressions.constant';
import { turnoverChartOptions } from '../../../../../../constants/turnover-chart-options.constants';
import { MainTabBasePathEnum } from '../../../../../../enums/main-tabs';
import {
  PrimarySubscriptionAccessActionEnum,
  PrimarySubscriptionPlanAccessAliasEnum
} from '../../../../../../enums/monetization';
import { ProfileSearchEnum } from '../../../../../../enums/profile-search';
import { WalkingFarmExportReportEnum } from '../../../../../../enums/walking-farm';
import * as fromWalkingFarmActions from '../../../../../../store/actions/walking-farm.actions';
import * as fromChartSelectors from '../../../../../../store/selectors/chart.selectors';
import * as fromOrganizationSelectors from '../../../../../../store/selectors/organization.selectors';
import { WalkingFarm, WalkingFarmProperty } from '../../../../../../typings/walking-farm';
import { MonetizationService } from '../../../../../services/monetization.service';
import { RenameFarmModalComponent } from '../rename-farm-modal/rename-farm-modal.component';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'red-walking-farm-view-details',
  standalone: false,
  templateUrl: './details.component.html'
})
export class DetailsComponent implements OnChanges {
  private activatedRoute = inject(ActivatedRoute);
  private actionSheetController = inject(ActionSheetController);
  private modalController = inject(ModalController);
  private monetizationService = inject(MonetizationService);
  private store = inject(Store);

  @Input() walkingFarmId: string | null = null;
  @Input() walkingFarm: WalkingFarm | null = null;
  @Input() isNonDisclosureState = false;

  chartColors: string[] = [];

  turnoverChartOptions = cloneDeep(turnoverChartOptions);

  ownerOccupiedChartOptions: EChartsOption = {
    graphic: {
      elements: [
        {
          right: 0,
          style: {
            align: 'left',
            fill: '#333',
            fontWeight: 'normal',
            height: 24,
            text: 'By Owner Status:',
            width: 100
          },
          top: '0',
          type: 'text'
        }
      ]
    },
    legend: {
      align: 'left',
      itemStyle: {
        borderColor: '#333',
        borderType: 'solid',
        borderWidth: 1
      },
      orient: 'vertical',
      right: '0',
      selectedMode: false,
      textStyle: {
        color: '#333',
        overflow: 'break',
        width: '100'
      },
      top: '24',
      width: '50%'
    },
    series: [
      {
        avoidLabelOverlap: true,
        center: ['50%', '50%'],
        data: [],
        label: {
          show: false
        },
        labelLine: {
          show: false
        },
        radius: ['80%', '95%'],
        type: 'pie',
        width: '50%'
      }
    ],
    title: {
      left: '24.5%',
      subtext: '0%',
      subtextStyle: {
        color: '#000',
        fontWeight: 'bold'
      },
      text: 'Occupied:',
      textAlign: 'center',
      textStyle: {
        fontSize: 12,
        fontWeight: 'normal'
      },
      textVerticalAlign: 'middle',
      top: '36.5%',
      width: '50%'
    }
  };

  turnoverRateEnabled = true;

  medianSalePrice: number | null = null;
  maxSalePrice: number | null = null;
  minSalePrice: number | null = null;
  totalSalesInLast12Months: number | null = null;
  turnoverRateInLast12Months: number | null = null;

  totalOwnerOccupied: number | null = null;
  totalOwnerAbsentee: number | null = null;
  totalOwnerOccupiedPercentage: number | null = null;

  medianBuildingArea: number | null = null;
  medianBedrooms: number | null = null;
  medianBathrooms: number | null = null;

  saleInformationEnabled$: Observable<boolean>;

  defaultBackHref: string | undefined;

  constructor() {
    this.defaultBackHref = this.activatedRoute.snapshot.data['defaultBackHref'];

    combineLatest([
      this.store.pipe(select(fromChartSelectors.selectColors)),
      this.store.pipe(select(fromOrganizationSelectors.selectTurnoverRateEnabled))
    ]).subscribe(([chartColors, turnoverRateEnabled]) => {
      this.turnoverRateEnabled = turnoverRateEnabled;
      this.chartColors = chartColors;
      this.updateChartsData();
    });
    this.saleInformationEnabled$ = this.store.pipe(select(fromOrganizationSelectors.selectSaleInformationEnabled));
  }

  getPropertiesMedianValue(key: keyof WalkingFarmProperty) {
    let result: number | null = null;
    if (this.walkingFarm) {
      const values = filter(
        map(this.walkingFarm.properties, (item) => parseFloat(`${item[key]}`?.replace(selectNonDigitCharacters, ''))),
        (item) => !isNaN(item)
      ).sort((a, b) => a - b);
      if (values.length) {
        result = values[floor(values.length / 2)];
      }
    }
    return result;
  }

  getValidNumericPropertiesValues(key: keyof WalkingFarmProperty) {
    let values: number[] = [];
    if (this.walkingFarm) {
      values = filter(
        map(this.walkingFarm.properties, (item) => parseFloat(`${item[key]}`?.replace(selectNonDigitCharacters, ''))),
        (item) => !isNaN(item) && item > 0
      ).sort((a, b) => a - b);
    }
    return values;
  }

  getTotalPropertiesSoldInLast12Months() {
    let total = 0;
    if (this.walkingFarm) {
      const oneYearAgoDate = DateTime.now().minus({ month: 12 });
      total = filter(this.walkingFarm.properties, (item) => {
        let result: boolean;
        if (isEmpty(item.A030_SaleDate)) {
          result = false;
        } else {
          const saleDate = DateTime.fromJSDate(new Date(item.A030_SaleDate));
          result = saleDate.isValid && saleDate > oneYearAgoDate;
        }
        return result;
      }).length;
    }
    return total;
  }

  ngOnChanges(changes: SimpleChanges) {
    if ((has(changes, 'walkingFarm') || has(changes, 'isNonDisclosureState')) && !isNull(this.walkingFarm)) {
      this.medianBuildingArea = this.getPropertiesMedianValue('A022_BuildingArea');
      this.medianBedrooms = this.getPropertiesMedianValue('A024_Bedrooms');
      this.medianBathrooms = this.getPropertiesMedianValue('A025_Bathrooms');

      const salePrices = this.getValidNumericPropertiesValues('A029_SalesPrice');

      if (this.isNonDisclosureState) {
        this.maxSalePrice = null;
        this.minSalePrice = null;
        this.medianSalePrice = null;
      } else {
        this.maxSalePrice = last(salePrices) ?? null;
        this.minSalePrice = first(salePrices) ?? null;
        this.medianSalePrice = salePrices[floor(salePrices.length / 2)] ?? null;
      }

      this.totalSalesInLast12Months = this.getTotalPropertiesSoldInLast12Months();
      this.turnoverRateInLast12Months = round(
        (this.totalSalesInLast12Months * 100) / this.walkingFarm.properties.length,
        2
      );

      this.totalOwnerOccupied = filter(this.walkingFarm.properties, (item) => item.A040_OwnerOccupied !== 'N').length;
      this.totalOwnerAbsentee = this.walkingFarm.properties.length - this.totalOwnerOccupied;

      if (this.totalOwnerOccupied > 0) {
        this.totalOwnerOccupiedPercentage = round(
          (this.totalOwnerOccupied * 100) / this.walkingFarm.properties.length,
          2
        );
      } else {
        this.totalOwnerOccupiedPercentage = 0;
      }

      this.updateChartsData();
    }
  }

  updateChartsData() {
    (this.turnoverChartOptions.series as SeriesOption[])[0].data = [{ value: this.turnoverRateInLast12Months ?? 0 }];
    (this.ownerOccupiedChartOptions.title as TitleOption).subtext = `${this.totalOwnerOccupiedPercentage}%`;

    (this.ownerOccupiedChartOptions.series as SeriesOption[])[0].data = [
      {
        name: `Occupied: ${this.totalOwnerOccupied ?? 'N/A'}`,
        value: this.totalOwnerOccupied ?? 0
      },
      {
        name: `Absentee: ${this.totalOwnerAbsentee ?? 'N/A'}`,
        value: this.totalOwnerAbsentee ?? 0
      }
    ];
  }

  async renameFarm() {
    const modal = await this.modalController.create({
      component: RenameFarmModalComponent,
      componentProps: {
        farmId: this.walkingFarmId || '',
        name: this.walkingFarm?.name || ''
      }
    });

    await modal.present();
  }

  async exportFarm() {
    const actionSheet = await this.actionSheetController.create({
      buttons: [
        {
          icon: 'pricetags',
          role: WalkingFarmExportReportEnum.Labels,
          text: 'Avery 5160 Labels'
        },
        {
          icon: '/assets/svg/pdf-file.svg',
          role: WalkingFarmExportReportEnum.Pdf,
          text: 'Walking Farm PDF'
        },
        {
          icon: '/assets/svg/csv-file.svg',
          role: WalkingFarmExportReportEnum.Csv,
          text: 'Spreadsheet CSV'
        },
        {
          icon: 'copy',
          role: WalkingFarmExportReportEnum.Duplicate,
          text: 'Duplicate Farm Settings'
        },
        {
          data: {
            action: 'cancel'
          },
          role: 'cancel',
          text: 'Cancel'
        }
      ],
      header: 'Export Farm',
      subHeader: 'Choose the desired type of export'
    });

    await actionSheet.present();
    const { role } = await actionSheet.onDidDismiss();
    if (!isNull(this.walkingFarmId)) {
      switch (role) {
        case WalkingFarmExportReportEnum.Labels:
          this.store.dispatch(
            fromWalkingFarmActions.exportLabels({
              payload: {
                farmId: this.walkingFarmId,
                name: `${snakeCase([this.walkingFarm?.name, 'Labels', DateTime.now().toFormat('X')].join(' '))}.pdf`
              }
            })
          );
          break;
        case WalkingFarmExportReportEnum.Pdf:
          this.store.dispatch(
            fromWalkingFarmActions.exportPDF({
              payload: {
                farmId: this.walkingFarmId,
                name: `${snakeCase([this.walkingFarm?.name, DateTime.now().toFormat('X')].join(' '))}.pdf`
              }
            })
          );
          break;
        case WalkingFarmExportReportEnum.Csv:
          this.store.dispatch(
            fromWalkingFarmActions.exportCSV({
              payload: {
                farmId: this.walkingFarmId,
                name: `${snakeCase([this.walkingFarm?.name, DateTime.now().toFormat('X')].join(' '))}.csv`
              }
            })
          );
          break;
        case WalkingFarmExportReportEnum.Duplicate:
          if (this.walkingFarm) {
            const hasAccess = await this.monetizationService.accessFeature([PrimarySubscriptionPlanAccessAliasEnum.C], {
              accessAction: PrimarySubscriptionAccessActionEnum.CreateFarm
            });
            if (hasAccess) {
              this.store.dispatch(
                fromWalkingFarmActions.newConfiguredFarm({
                  payload: {
                    searchType:
                      this.defaultBackHref === MainTabBasePathEnum.PropertySearch
                        ? ProfileSearchEnum.PropertySearchWalkingFarm
                        : ProfileSearchEnum.WalkingFarm,
                    shapeCoordinates: this.walkingFarm.shapeCoordinates
                  }
                })
              );
            }
          }
          break;
        default:
          break;
      }
    }
  }
}
